'use client';

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useActiveFolderId } from '@frameio/shared/contexts';
import { getSourceFromPath } from '@frameio/shared/integrations/utils/trackingUtils';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { Chip } from '@frameio/vapor';
import { IconWarning } from '@frameio/vapor-icons';
import {
  ExportDialogContainer,
  ExportSettingsMetadata,
} from '../../../shared/components/DocumentExports';
import {
  SettingRow,
  UploadLocationRow,
  UploadMarkersRow,
} from '../../../shared/components/DocumentExports/ExportFormFields';
import { useOnResourceSelect } from '../../../shared/components/ExportLocation';
import type { PendingExport } from '../../contexts/CompositionExportContext';
import { useGetRenderQueueItemCountQuery } from '../../gql';
import { useCompositionExportSettings } from '../../hooks';
import TEXT from './generated/TEXT/CompositionExportAll';

const RENDER_QUEUE_COUNT_POLL_INTERVAL_MS = 200;

TEXT.DEFINE({
  /** CTA to choose export settings in Render Queue */
  renderSettingsCTA: 'Output settings are managed in the Render Queue panel.',
  /** Form validation error message when no active composition is present */
  noComposition: 'No active composition',
  /** Button label to start the export process (Render + Upload) */
  renderAndUpload: 'Render & Upload',
  /** Label for the number of compositions */
  compositions: 'Compositions',
  /** Label for the number of compositions queued for export */
  queued: 'Queued',
  /** Error message shown when the render queue is empty */
  emptyRenderQueue: 'No items in the render queue',
});

type CompositionExportProps = {
  onCancel: () => void;
  onScheduleExport: (params: PendingExport) => void;
  overrideFolderId?: string;
};

export const CompositionExportAll = ({
  onCancel: onClose,
  onScheduleExport,
  overrideFolderId,
}: CompositionExportProps) => {
  const track = useTrackUnsafe();
  const pathname = usePathname();
  const activeFolderId = useActiveFolderId();
  const { onResourceSelect } = useOnResourceSelect();
  const { data: renderQueueItemCountData, refetch: fetchRenderQueueItemCount } =
    useGetRenderQueueItemCountQuery({
      context: { resolveWithUXP: true },
      pollInterval: RENDER_QUEUE_COUNT_POLL_INTERVAL_MS,
      fetchPolicy: 'network-only',
    });

  const renderQueueItemCount =
    renderQueueItemCountData?.getRenderQueueItemCount ?? 0;

  const {
    assetFolderId,
    assetFolderName,
    exportMarkersAsCommentsEnabled,
    loading: isExportSettingsLoading,
    onProjectIdChange,
    projectId,
    settingsError,
    storeExportSettings,
    updateExportSettings,
  } = useCompositionExportSettings({ overrideFolderId });

  const onUploadLocationOpen = useCallback(() => {
    track('frameio-destination-button-clicked', {
      source: getSourceFromPath(pathname, 'aftereffects'),
    });
  }, [track, pathname]);

  const handleExport = useCallback(async () => {
    if (!assetFolderId) return;

    track('export-started', {
      source: getSourceFromPath(pathname, 'aftereffects'),
      is_upload_markers_enabled: Boolean(exportMarkersAsCommentsEnabled),
      type: 'export all',
    });

    const { data } = await fetchRenderQueueItemCount();

    onScheduleExport({
      jobId: crypto.randomUUID(),
      assetFolderId,
      projectId,
      compositionRenderingCount: data?.getRenderQueueItemCount ?? 0,
      exportMarkersAsCommentsEnabled: !!exportMarkersAsCommentsEnabled,
      onAfterExport: () =>
        storeExportSettings({ assetFolderId, assetFolderName }),
    });
  }, [
    assetFolderId,
    assetFolderName,
    exportMarkersAsCommentsEnabled,
    fetchRenderQueueItemCount,
    onScheduleExport,
    pathname,
    projectId,
    storeExportSettings,
    track,
  ]);

  const handleSubmit = useCallback(async () => {
    onResourceSelect({
      data: { folderId: assetFolderId ?? '' },
      onSuccess: handleExport,
    });
  }, [handleExport, assetFolderId, onResourceSelect]);

  const errorMessage =
    renderQueueItemCount < 1 ? TEXT.emptyRenderQueue : settingsError;

  return (
    <ExportDialogContainer
      error={errorMessage}
      isLoading={isExportSettingsLoading}
      metadata={
        <ExportSettingsMetadata
          metadata={[
            { key: 'renderSettingsCTA', value: TEXT.renderSettingsCTA },
          ]}
        />
      }
      onCancel={onClose}
      onSubmit={handleSubmit}
      submitButtonLabel={TEXT.renderAndUpload}
    >
      <SettingRow
        field={
          <div style={{ marginLeft: 'auto' }}>
            <Chip
              color={renderQueueItemCount > 0 ? 'blue' : 'red'}
              iconBefore={
                renderQueueItemCount > 0 ? undefined : <IconWarning />
              }
            >{`${renderQueueItemCount} ${TEXT.queued}`}</Chip>
          </div>
        }
        title={TEXT.compositions}
      />
      <UploadLocationRow
        disabled={false}
        disabledTooltip={undefined}
        folderId={assetFolderId ?? null}
        name={assetFolderName ?? undefined}
        onFolderIdChange={(id) => {
          updateExportSettings({ assetFolderId: id });
        }}
        onOpen={onUploadLocationOpen}
        onProjectIdChange={onProjectIdChange}
        projectId={projectId}
        startInFoldersView={!!activeFolderId}
      />
      <UploadMarkersRow
        enabled={!!exportMarkersAsCommentsEnabled}
        onEnabledChange={(enabled: boolean) => {
          updateExportSettings({
            exportMarkersAsCommentsEnabled: enabled,
          });
        }}
      />
    </ExportDialogContainer>
  );
};
